/**
 * ProfileTab — the ContactRail's third tab (Expansion Phase G): who this
 * patient IS, as opposed to what to do next (Next steps) or the live screen
 * (Screening). Completeness meter + what's-missing hints, medications,
 * diagnoses, call ratings, web-screener answers (absorbed from the Screening
 * tab), per-study participation history, and automation enrollments (moved
 * from Next steps — enrollment state is history, not a next step).
 */
import { useState } from 'react';
import { FileDown } from 'lucide-react';
import { ContactDetailResponse, Enrollment } from '../../types';
import ScreenerPanel from '../ScreenerPanel';
import MedicalRecordsCard from '../records/MedicalRecordsCard';
import EligibilityReviewCard from '../eligibility/EligibilityReviewCard';
import ProfileListCard from './ProfileListCard';
import LabsCard from './LabsCard';
import RatingsCard from './RatingsCard';
import ParticipationHistoryCard from './ParticipationHistoryCard';
import EnrollmentsCard from './EnrollmentsCard';
import PrescreeningSummaryModal from './PrescreeningSummaryModal';

const MISSING_HINTS: Record<string, string> = {
  phone: 'Add a phone number',
  email: 'Add an email address',
  medications: 'Record their medications',
  conditions: 'Record their diagnoses',
  completed_form: 'Have them complete a form',
  answers: 'No screener answers yet — run a prescreen or send a form',
};

interface Props {
  detail: ContactDetailResponse;
  enrollments: Enrollment[];
  reloadToken: number;
  /** Debounced Realtime ping (ContactDetail) — silent refetch of the records list. */
  pingTick?: number;
  isSuperAdmin: boolean;
  reenrolling: boolean;
  onReEnroll: () => void;
}

export default function ProfileTab({
  detail, enrollments, reloadToken, pingTick = 0, isSuperAdmin, reenrolling, onReEnroll,
}: Props) {
  const c = detail.contact;
  const completeness = detail.profile_completeness || null;
  const [exportOpen, setExportOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Profile</h3>
        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
        >
          <FileDown className="h-4 w-4" />
          Export prescreening summary
        </button>
      </div>
      {exportOpen && <PrescreeningSummaryModal contactId={c.id} onClose={() => setExportOpen(false)} />}

      {completeness && completeness.missing.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Profile completeness</h3>
            <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">{completeness.percent}%</span>
          </div>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
            role="progressbar"
            aria-valuenow={completeness.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Profile completeness"
          >
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${completeness.percent}%` }} />
          </div>
          <ul className="mt-2 space-y-0.5">
            {completeness.missing.map((key) => (
              <li key={key} className="text-xs text-gray-500 dark:text-gray-400">· {MISSING_HINTS[key] || key}</li>
            ))}
          </ul>
        </div>
      )}

      {/* The source documents the meds/diagnoses/labs below are extracted from —
          faxed-in records + files uploaded here (reviewed by the nightly AI). */}
      <MedicalRecordsCard contactId={c.id} reloadToken={reloadToken} pingTick={pingTick} />

      {/* AI eligibility reviews of everything above (records + comms + forms)
          against a study's confirmed review criteria. */}
      <EligibilityReviewCard
        key={c.id}
        contactId={c.id}
        pingTick={pingTick}
        primaryStudyId={c.primary_study_id ?? null}
      />

      <ProfileListCard kind="medications" contactId={c.id} reloadToken={reloadToken} />
      <ProfileListCard kind="conditions" contactId={c.id} reloadToken={reloadToken} />
      <LabsCard contactId={c.id} reloadToken={reloadToken} />
      <RatingsCard contactId={c.id} reloadToken={reloadToken} />
      <ParticipationHistoryCard contactId={c.id} reloadToken={reloadToken} />
      {/* Web screener answers — absorbed here from the Screening tab. */}
      <ScreenerPanel screener={detail.screener} />
      <EnrollmentsCard
        enrollments={enrollments}
        isSuperAdmin={isSuperAdmin}
        reenrolling={reenrolling}
        onReEnroll={onReEnroll}
      />
    </div>
  );
}
